import { Box, Chip, Divider, Typography } from '@mui/material';
import { FC } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const Introduction: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Introduction
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Welcome to the Thermia Developer Portal. This documentation will help you get started integrating with the Thermia API to read data from and
      control Thermia heat pump devices.
    </Typography>

    <DocSection title="What is the Thermia API?">
      <Typography variant="body1" gutterBottom>
        The Thermia API is a REST API that allows you to interact programmatically with Thermia Online-connected heat pumps. You can retrieve
        real-time operational data, historical readings, and send control commands — all through simple HTTP requests.
      </Typography>
    </DocSection>

    <DocSection title="Base URL">
      <DocCodeBlock language="text">https://api.thermiaonline.com/v1</DocCodeBlock>
    </DocSection>

    <DocSection title="Key Capabilities">
      <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
        <li>
          <Typography variant="body1">
            <strong>Read device data</strong> — Fetch current temperatures, operating modes, and status.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Control devices</strong> — Adjust set-points and operating modes remotely.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Historical data</strong> — Retrieve time-series sensor data for analysis and reporting.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Webhooks</strong> — Subscribe to real-time event notifications when device state changes.
          </Typography>
        </li>
      </Box>
    </DocSection>

    <DocSection title="API Versions">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[
          {
            version: 'v1',
            status: 'Current',
            color: 'success' as const,
            description: 'Latest stable version — recommended for all new integrations.'
          },
          { version: 'v0', status: 'Deprecated', color: 'warning' as const, description: 'Legacy version. Will be retired on 1 January 2027.' }
        ].map(({ version, status, color, description }) => (
          <Box key={version} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Chip label={status} color={color} size="small" sx={{ mt: 0.3, minWidth: 90 }} />
            <Typography variant="body1">
              <strong>{version}</strong> — {description}
            </Typography>
          </Box>
        ))}
      </Box>
    </DocSection>

    <Divider sx={{ my: 3 }} />

    <DocCallout type="info">
      This portal is intended for third-party developers and integrators building on top of the Thermia platform. If you are an end user of Thermia
      Online, please visit{' '}
      <a href="https://www.thermia.com" target="_blank" rel="noreferrer">
        thermia.com
      </a>{' '}
      instead.
    </DocCallout>
  </Box>
);

export default Introduction;
